import {
    Box,
    Flex,
    Icon,
    Icons,
    Tab,
    TabList,
    ThemeColorVariable,
    ThemeIconSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import sideNavigate from "components/selection-components/preorder/side-navigate";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Add new interface for imperative handle
export interface TabListHandle {
    scrollTabIntoView: (tabIndex: number) => void;
}

interface TabListComponentProps {
    items: Array<{
        id: string | number;
        name: string;
        highlighted?: boolean;
    }>;
    onTabChange?: (index: number) => void;
}

const TabListComponent = forwardRef<TabListHandle, TabListComponentProps>(({ items }, ref) => {
    const { t } = useTranslation();
    const tabListContainerRef = useRef<HTMLDivElement>(null);
    const [visibleNavButtons, setVisibleNavButtons] = useState<{
        left: boolean;
        right: boolean;
    }>({ left: false, right: true });
    const [scrollbarHeight, setScrollbarHeight] = useState(0);

    useImperativeHandle(ref, () => ({
        scrollTabIntoView: (tabIndex: number) => {
            const scrollableDiv = tabListContainerRef.current;
            if (!scrollableDiv) return;

            const tabElements = Array.from(scrollableDiv.querySelectorAll('[role="tab"]'));
            if (!tabElements || tabIndex >= tabElements.length) return;

            const targetTab = tabElements[tabIndex] as HTMLElement;
            if (!targetTab) return;

            const tabLeft = targetTab.offsetLeft;
            const tabRight = tabLeft + targetTab.offsetWidth;
            const containerLeft = scrollableDiv.scrollLeft;
            const containerRight = containerLeft + scrollableDiv.clientWidth;

            const buffer = 66; // 36px buffer, medium space * 2 for paddings + 30px for getting it in view
            if (tabLeft < containerLeft) {
                // Tab is to the left - scroll left
                const scrollDistance = containerLeft - tabLeft + buffer;
                sideNavigate(tabListContainerRef, "left", 10, scrollDistance, 10);
                setTimeout(updateNavigationButtonVisibility, 500);
            } else if (tabRight > containerRight) {
                // Tab is to the right - scroll right
                const scrollDistance = tabRight - containerRight + buffer;
                sideNavigate(tabListContainerRef, "right", 10, scrollDistance, 10);
                setTimeout(updateNavigationButtonVisibility, 500);
            }
        },
    }));

    const onClickNavigateLeft = () => {
        if (tabListContainerRef.current) {
            sideNavigate(tabListContainerRef, "left", 10, 450, 10);

            // Update buttons after animation completes
            setTimeout(updateNavigationButtonVisibility, 500);
        }
    };

    const onClickNavigateRight = () => {
        if (tabListContainerRef.current) {
            sideNavigate(tabListContainerRef, "right", 10, 450, 10);

            // Update buttons after animation completes
            setTimeout(updateNavigationButtonVisibility, 500);
        }
    };

    // Extract this function so it can be passed as a callback
    const updateNavigationButtonVisibility = () => {
        const scrollableDiv = tabListContainerRef.current;
        if (!scrollableDiv) return;

        const isScrollable = scrollableDiv.scrollWidth > scrollableDiv.clientWidth;

        // Calculate and store scrollbar height
        const currentScrollbarHeight = scrollableDiv.offsetHeight - scrollableDiv.clientHeight;
        setScrollbarHeight(currentScrollbarHeight);

        if (!isScrollable) {
            setVisibleNavButtons({ left: false, right: false });
            return;
        }

        const maxScrollLeft = scrollableDiv.scrollWidth - scrollableDiv.clientWidth;
        const scrollLeft = Math.ceil(scrollableDiv.scrollLeft);

        // Use a margin of 15px
        if (scrollLeft <= 15) {
            setVisibleNavButtons({ left: false, right: true });
        } else if (scrollLeft >= maxScrollLeft - 15) {
            setVisibleNavButtons({ left: true, right: false });
        } else {
            setVisibleNavButtons({ left: true, right: true });
        }
    };

    useEffect(() => {
        const scrollableDiv = tabListContainerRef.current;

        window.addEventListener("resize", updateNavigationButtonVisibility);
        scrollableDiv?.addEventListener("scroll", updateNavigationButtonVisibility);

        // Initial check
        updateNavigationButtonVisibility();
        return () => {
            window.removeEventListener("resize", updateNavigationButtonVisibility);
            scrollableDiv?.removeEventListener("scroll", updateNavigationButtonVisibility);
        };
    }, []);

    return (
        <Flex align="center" position="relative" justifyContent="flex-start" width="100%">
            {
                <Box
                    position="absolute"
                    left={0}
                    zIndex={2}
                    height="100%"
                    background={`linear-gradient(to right, ${ThemeColorVariable.Background} 70%, transparent)`}
                    visibility={visibleNavButtons.left ? "visible" : "hidden"}
                    display="flex"
                    alignItems="center"
                    paddingBottom={
                        scrollbarHeight
                            ? `calc(${scrollbarHeight}px + ${ThemeIconSizeVariable.Medium} / 2)`
                            : `calc(${ThemeIconSizeVariable.Medium} / 2)`
                    }
                >
                    <Box
                        aria-label={t("tablist.actionNavigateLeft")}
                        onClick={onClickNavigateLeft}
                        h="100%"
                        padding={`0 ${ThemeSpaceVariable.Medium}`}
                        paddingBottom={scrollbarHeight ? `${scrollbarHeight}px` : 0}
                        background="transparent"
                        as="button"
                    >
                        <Icon
                            size={ThemeIconSizeVariable.Medium}
                            icon={Icons.ChevronLeft}
                            color={ThemeColorVariable.OnBackground}
                        />
                    </Box>
                </Box>
            }

            <Box
                ref={tabListContainerRef}
                width="100%"
                overflowX="auto"
                overflowY="hidden"
                position="relative"
            >
                <TabList
                    mb={ThemeSpaceVariable.Medium}
                    width="fit-content" // Allow content to determine width
                    minWidth="100%"
                    gap={ThemeSpaceVariable.Small}
                    pr={ThemeSpaceVariable.Medium}
                    pl={ThemeSpaceVariable.Medium}
                >
                    {items.map((item, idx) => (
                        <Tab key={`${item.id}-${idx}`}>
                            {item.name}
                            {item.highlighted ? " *" : ""}
                        </Tab>
                    ))}
                </TabList>
            </Box>

            {
                <Box
                    position="absolute"
                    right={0}
                    zIndex={2}
                    height="100%"
                    background={`linear-gradient(to left, ${ThemeColorVariable.Background} 70%, transparent)`}
                    visibility={visibleNavButtons.right ? "visible" : "hidden"}
                    display="flex"
                    alignItems="center"
                    paddingBottom={
                        scrollbarHeight
                            ? `calc(${scrollbarHeight}px + ${ThemeIconSizeVariable.Medium} / 2)`
                            : `calc(${ThemeIconSizeVariable.Medium} / 2)`
                    }
                >
                    <Box
                        aria-label={t("tablist.actionNavigateRight")}
                        onClick={onClickNavigateRight}
                        h="100%"
                        padding={`0 ${ThemeSpaceVariable.Medium}`}
                        paddingBottom={scrollbarHeight ? `${scrollbarHeight}px` : 0}
                        background="transparent"
                        as="button"
                    >
                        <Icon icon={Icons.ChevronRight} color={ThemeColorVariable.OnBackground} />
                    </Box>
                </Box>
            }
        </Flex>
    );
});

export default TabListComponent;
